import * as TableBasedInventory from 'types/tableBasedInventory';
import * as TableBasedTemplates from 'types/tableBasedTemplates';
import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import {
  SeatingTemplateTable,
  TEMPLATE_INFO_BUSINESS_DAY,
  getConditionalRangeTemplateForSTTTicketTypeConfig,
  rangeSetFromRestrictions,
} from 'state/templateInfo';
import { adjustForBusinessDay, adjustRangeForBusinessDay } from 'utils/timeUtils';
import { archivedExperienceColor } from 'constants/UIConstants';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getColorOfTicketTypeId } from 'utils/templateUtils';
import { getDefaultTurnTimeFields } from 'components/inventory-schedule-turn-times/TurnTimeFieldset';
import { getDisplayColorValueFromTicketType } from 'utils/customExperienceColorsUtils';
import { getInheritedExperienceInfoOnSeatingTemplateTableCombination } from 'selectors/routes/table-based-templates/seatingTemplateTableCombinationSelector';
import RangeSet, { range } from 'utils/range/RangeSet';
import _ from 'lodash';
import natsort from 'natsort';

/**
 * This file contains utility functions that manipulate our pending schedule data which is in a
 * local reducer.  These are essentially "selectors" for our useReducer inventory schedule.
 */
export interface ExperienceTableCount {
  count: number;
  name: string;
}

export interface NamedTableCombination extends dashboard.ITableCombination {
  name: string;
}

export const INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID = -1;

type ExperienceAssignment = { ticketTypeId: number; displayColor: string; name: string };

interface SummaryRecord {
  name: string;
  numTables: number;
  maxCovers: number;
  usesTablePriority: boolean;
  numTableCombos: number;
}

export interface ExperienceSummary extends SummaryRecord {
  displayColor: string;
  id: number;
  rooms: { [room: string]: SummaryRecord };
}

export interface ExperienceSummaryMap {
  [ticketTypeId: number]: ExperienceSummary;
}

interface AssignableTable {
  id: number;
  isArchived: false;
  size: number;
  room: string;
}

interface TableCombinationWithRoom extends dashboard.ISeatingTemplateTableCombination {
  room: string;
}
/**
 * Type guard to make sure ITables have values that are required to have a valid experience assignment
 * */
const isAssignableTable = (table: dashboard.ITable): table is AssignableTable => {
  return !!(table.name && !table.isArchived && _.isNumber(table.id) && _.isNumber(table.size));
};

/**
 * Create a rollup of experience assignments that currently exist on a seating template.
 * To build the rollup we attempt to find a SeatingTemplateTable entry for each active floor plan table.
 * Tables we cannot find are counted as inactive.
 *
 * Each rollup contains the total number of tables and max possible covers as well as these
 * values grouped by room.
 */
export const getExperienceSummaries = (
  seatingTemplateId: number,
  ticketTypes: Dictionary<core.TicketType>,
  { floorplan, seatingTemplate }: dashboard.BusinessInventorySchedule
): ExperienceSummary[] | undefined => {
  const activeSeatingTemplate = seatingTemplate.find((t) => t.id === seatingTemplateId);
  const activeFloorplanTables = floorplan?.table?.filter((t) => !t.isArchived) ?? [];

  // If we don't have any tables or can't find the seatingTemplate get out
  if (activeFloorplanTables.length === 0 || !activeSeatingTemplate) {
    return;
  }

  // Makes a map of tableId => seatingTemplateTables for faster lookups
  const templateTableMap = (activeSeatingTemplate.table ?? []).reduce<
    Dictionary<dashboard.ISeatingTemplateTable>
  >((map, t) => (t.tableId ? { ...map, [t.tableId]: t } : map), {});
  const allActiveTickets = _.values(ticketTypes);

  // Gets a map of all rooms with prioritized tables by ticketTypeId
  const roomsWithPrioritizedTablesByTicketTypeId = activeFloorplanTables.reduce<
    Dictionary<string[]>
  >((acc, table) => {
    if (!isAssignableTable(table)) {
      return acc;
    }

    const templateTable = templateTableMap[table.id];

    // bail if templateTable is null or ticketTypeIds is null / empty
    if (!templateTable?.ticketTypeId || templateTable.ticketTypeId.length === 0) {
      return acc;
    }

    templateTable.ticketTypeId.forEach((ticketTypeId) => {
      // assign an empty array value for this ticketTypeId key

      if (!acc[ticketTypeId]) {
        acc[ticketTypeId] = [];
      }

      // add the room if it hasn't been added yet
      if (
        templateTable.assignmentPriorityLevel !== 'NEUTRAL' &&
        !acc[ticketTypeId].includes(table.room)
      ) {
        acc[ticketTypeId].push(table.room);
      }
    });

    return acc;
  }, {});

  const roomsByTableId = new Map(activeFloorplanTables.map((table) => [table.id, table.room]));

  const inheritedTicketTypeInfoByTemplateTableComboId =
    getInheritedExperienceInfoOnSeatingTemplateTableCombination(
      activeSeatingTemplate as dashboard.SeatingTemplate,
      activeSeatingTemplate.table as SeatingTemplateTable[],
      activeSeatingTemplate.tableCombination as dashboard.SeatingTemplateTableCombination[],
      floorplan?.tableCombination as dashboard.TableCombination[]
    );

  const tableCombosWithRoomByExperience = new Map<number, TableCombinationWithRoom[]>();

  let numInactiveTableCombos = 0;

  activeSeatingTemplate.tableCombination
    ?.map((seatingTemplateTableCombo) => {
      const tableComboTableIds = floorplan?.tableCombination?.find(
        (combo) => combo.id === seatingTemplateTableCombo.tableCombinationId
      )?.tableId;

      const templateTableTableIds =
        activeSeatingTemplate.table
          ?.filter((t) => !t.ticketTypeId?.includes(INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID))
          .map((table) => table?.tableId) ?? [];

      if (
        !tableComboTableIds?.every(
          (tableId) =>
            templateTableTableIds.includes(tableId) && roomsByTableId.has(tableComboTableIds[0])
        )
      ) {
        // check how many inactive table combos there are
        let containsInactiveTable = false;
        tableComboTableIds?.map((tableId) => {
          const floorplanTableComboTableIds = floorplan?.tableCombination?.find(
            (floorplanCombo) => floorplanCombo.id === seatingTemplateTableCombo.tableCombinationId
          )?.tableId;
          if (floorplanTableComboTableIds?.includes(tableId)) {
            containsInactiveTable = true;
          }
        });
        if (containsInactiveTable) {
          numInactiveTableCombos += 1;
        }
        return undefined;
      } else {
        return {
          ...seatingTemplateTableCombo,
          room: roomsByTableId.get(tableComboTableIds[0]) ?? '',
        };
      }
    })
    .filter(filterNilValues)
    .forEach((tableComboWithRoom) => {
      const ticketTypeConfigs = tableComboWithRoom.ticketTypeConfig ?? [];
      const hasOwnExperiences = ticketTypeConfigs.length > 0;

      const inheritedTicketTypeInfo = tableComboWithRoom.tableCombinationId
        ? (inheritedTicketTypeInfoByTemplateTableComboId.get(
            tableComboWithRoom.tableCombinationId
          ) ?? new Map<number, RangeSet<LocalDateTime>>())
        : new Map<number, RangeSet<LocalDateTime>>();

      const effectiveTicketTypeIds = hasOwnExperiences
        ? ticketTypeConfigs.map((config) => config.ticketTypeId).filter(filterNilValues)
        : Array.from(inheritedTicketTypeInfo.keys());

      effectiveTicketTypeIds.forEach((ticketTypeId) => {
        tableCombosWithRoomByExperience.has(ticketTypeId)
          ? tableCombosWithRoomByExperience.get(ticketTypeId)?.push(tableComboWithRoom)
          : tableCombosWithRoomByExperience.set(ticketTypeId, [tableComboWithRoom]);
      });
    });

  const experienceSummaryMap = activeFloorplanTables.reduce<ExperienceSummaryMap>((acc, table) => {
    if (!isAssignableTable(table)) {
      return acc;
    }

    const templateTable = templateTableMap[table.id];
    const experienceAssignment = {
      ticketTypeId: INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID,
      name: 'Inactive',
      colorClassName: getColorOfTicketTypeId(
        allActiveTickets,
        INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID
      ),
      displayColor: archivedExperienceColor,
    };

    // If no ticket type ids are present, table is inactive
    const effectiveTicketTypeIds =
      templateTable?.ticketTypeId && templateTable.ticketTypeId.length > 0
        ? templateTable?.ticketTypeId
        : [INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID];

    effectiveTicketTypeIds.forEach((ticketTypeId) => {
      if (templateTable && ticketTypeId && ticketTypes[ticketTypeId]) {
        experienceAssignment.ticketTypeId = ticketTypeId;
        experienceAssignment.name = ticketTypes[ticketTypeId].name;
        experienceAssignment.colorClassName = getColorOfTicketTypeId(
          allActiveTickets,
          ticketTypeId
        );
        experienceAssignment.displayColor = getDisplayColorValueFromTicketType(
          ticketTypes[ticketTypeId]
        );
      }

      const roomsWithPrioritizedTablesForThisTicketType =
        roomsWithPrioritizedTablesByTicketTypeId[experienceAssignment.ticketTypeId] ?? [];

      let isInactiveExperience = false;
      experienceAssignment.ticketTypeId === INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID
        ? (isInactiveExperience = true)
        : (isInactiveExperience = false);

      acc[experienceAssignment.ticketTypeId] = !acc[experienceAssignment.ticketTypeId]
        ? _createExperienceSummary(
            experienceAssignment,
            table.size,
            roomsWithPrioritizedTablesForThisTicketType.length > 0,
            table.room,
            roomsWithPrioritizedTablesForThisTicketType.includes(table.room),
            isInactiveExperience ? numInactiveTableCombos : 0
          )
        : _updateExperienceSummary(
            acc[experienceAssignment.ticketTypeId],
            table.size,
            table.room,
            roomsWithPrioritizedTablesForThisTicketType.includes(table.room),
            0
          );
    });

    Array.from(tableCombosWithRoomByExperience.entries()).forEach(
      ([ticketTypeId, tableComboWithRoom]) => {
        const tableComboWithRoomGroupedByRoom = _.groupBy(
          tableComboWithRoom,
          (tableComboWithRoom) => tableComboWithRoom.room
        );

        if (acc[Number(ticketTypeId)]) {
          const experienceSummary = acc[Number(ticketTypeId)];
          experienceSummary.numTableCombos = tableComboWithRoom.length;
          Object.entries(tableComboWithRoomGroupedByRoom).forEach(([room, tableCombos]) => {
            if (experienceSummary.rooms[room]) {
              experienceSummary.rooms[room].numTableCombos = tableCombos.length;
            } else {
              experienceSummary.rooms[room] = {
                name: room,
                numTables: 0,
                maxCovers: 0,
                usesTablePriority: false,
                numTableCombos: tableCombos.length,
              };
            }
          });
        } else {
          if (ticketTypeId && ticketTypes[ticketTypeId]) {
            experienceAssignment.ticketTypeId = ticketTypeId;
            experienceAssignment.name = ticketTypes[ticketTypeId].name;
            experienceAssignment.colorClassName = getColorOfTicketTypeId(
              allActiveTickets,
              ticketTypeId
            );
            experienceAssignment.displayColor = getDisplayColorValueFromTicketType(
              ticketTypes[ticketTypeId]
            );
          }
          acc[Number(ticketTypeId)] = _createExperienceSummaryFromExperienceOnCombo(
            experienceAssignment,
            tableComboWithRoom.length
          );
          acc[Number(ticketTypeId)].numTables = 0;
          Object.entries(tableComboWithRoomGroupedByRoom).forEach(([room, tableCombos]) => {
            acc[Number(ticketTypeId)].rooms[room] = {
              name: room,
              numTables: 0,
              maxCovers: 0,
              usesTablePriority: false,
              numTableCombos: tableCombos.length,
            };
          });
        }
      }
    );

    return acc;
  }, {});

  return Object.values(experienceSummaryMap).sort((a, b) => b.id - a.id); // Sort largest -> smallest id so inactive is always last
};

const _createExperienceSummaryFromExperienceOnCombo = (
  { displayColor, ticketTypeId, name }: ExperienceAssignment,
  numTableCombos: number
): ExperienceSummary => {
  return {
    id: ticketTypeId,
    displayColor,
    name,
    numTables: 0,
    maxCovers: 0,
    usesTablePriority: false,
    numTableCombos: numTableCombos,
    rooms: {},
  };
};

const _createExperienceSummary = (
  { displayColor, ticketTypeId, name }: ExperienceAssignment,
  size: number,
  usesTablePriority: boolean,
  room: string,
  roomUsesTablePriority: boolean,
  numTableCombos: number
): ExperienceSummary => {
  return {
    id: ticketTypeId,
    displayColor,
    name,
    numTables: 1,
    maxCovers: size,
    usesTablePriority: usesTablePriority,
    numTableCombos: numTableCombos,
    rooms: {
      [room]: {
        name: room,
        numTables: 1,
        maxCovers: size,
        usesTablePriority: roomUsesTablePriority,
        numTableCombos: numTableCombos,
      },
    },
  };
};

const _updateExperienceSummary = (
  summary: ExperienceSummary,
  size: number,
  room: string,
  roomUsesTablePriority: boolean,
  numTableCombos: number
) => {
  return {
    ...summary,
    numTables: summary.numTables + 1,
    maxCovers: summary.maxCovers + size,
    numTableCombos: summary.numTableCombos + numTableCombos,
    rooms: {
      ...summary.rooms,
      [room]: summary.rooms[room]
        ? {
            name: room,
            numTables: summary.rooms[room].numTables + 1,
            maxCovers: summary.rooms[room].maxCovers + size,
            usesTablePriority: roomUsesTablePriority,
            numTableCombos: summary.rooms[room].numTableCombos + numTableCombos,
          }
        : {
            name: room,
            numTables: 1,
            maxCovers: size,
            usesTablePriority: roomUsesTablePriority,
            numTableCombos: 0,
          },
    },
  };
};

/**
 * Returns the count of rooms and tables in a floor plan
 */
export const getFloorPlanSummary = (floorplan?: dashboard.IFloorplan | null) => {
  if (!floorplan || !floorplan.table) {
    return { numRooms: 0, numTables: 0 };
  }

  return {
    numRooms: getFloorPlanRooms(floorplan).length,
    numTables: floorplan.table.filter((t) => !t.isArchived).length,
  };
};

export const getFloorPlanRooms = (floorplan?: dashboard.IFloorplan | null): string[] => {
  if (!floorplan || !floorplan.table) {
    return [];
  }

  return floorplan.table
    .reduce<string[]>((acc, table) => {
      if (table.isArchived || !table.room) {
        return acc;
      }
      return acc.includes(table.room) ? acc : [table.room, ...acc];
    }, [])
    .sort();
};

export const getFloorPlanTablesForRoom = (
  floorplan?: dashboard.IFloorplan | null,
  room?: string
) => {
  if (!floorplan || !floorplan.table || !room) {
    return [];
  }

  return floorplan.table.reduce<dashboard.Table[]>(
    (acc, table) =>
      !table.isArchived && table.room === room ? [...acc, table as dashboard.Table] : acc,
    []
  );
};

export const getFloorPlanTablesByRoom = (floorplan?: dashboard.IFloorplan | null) => {
  if (!floorplan || !floorplan.table) {
    return {};
  }

  return _.groupBy(
    floorplan.table.filter((t) => !t.isArchived && t.room),
    'room'
  );
};
export const getFloorPlanSectionNamesForRoom = (
  floorplan?: dashboard.IFloorplan | null,
  room?: string
) => {
  if (!floorplan || !floorplan.table || !room) {
    return [];
  }

  return floorplan.table.reduce<string[]>(
    (acc, table) =>
      table.room === room && table.section && !acc.includes(table.section)
        ? [...acc, table.section]
        : acc,
    []
  );
};

export type FloorplanSectionsWithCurrentRoom =
  TableBasedInventory.Section<TableBasedTemplates.TableOrTableGroup> & {
    isInCurrentRoom?: boolean;
  };

export const getFloorPlanSectionsForRoom = (
  floorplan?: dashboard.IFloorplan | null,
  room?: string
) => {
  if (!floorplan || !floorplan.table || !room) {
    return [];
  }

  return Array.from(
    floorplan.table
      .reduce<Map<string, FloorplanSectionsWithCurrentRoom>>((acc, table) => {
        if (table.room === room && table.section) {
          acc.set(table.section, {
            elements: [],
            label: table.section,
            isInCurrentRoom: table.room === room,
          });
        }
        return acc;
      }, new Map<string, FloorplanSectionsWithCurrentRoom>())
      .values()
  );
};

export const getFloorPlanSeatingAreasForRoom = (
  floorplan?: dashboard.IFloorplan | null,
  room?: string
) => {
  if (!floorplan || !floorplan.table || !room) {
    return [];
  }

  return Array.from(
    floorplan.table
      .reduce<Map<string, dashboard.ISeatingArea>>((acc, table) => {
        if (table.room === room && table?.seatingArea?.enumValue) {
          acc.set(table.seatingArea.enumValue, table.seatingArea);
        }
        return acc;
      }, new Map<string, dashboard.ISeatingArea>())
      .values()
  );
};

export const getActiveTableCombinations = (
  floorplan?: dashboard.IFloorplan | null,
  room?: string
): NamedTableCombination[] => {
  if (!room || !floorplan || !floorplan.table || !floorplan.tableCombination) {
    return [];
  }

  const tableMap = floorplan.table.reduce<Record<number, dashboard.ITable>>((acc, t) => {
    if (t.id) {
      acc[t.id] = t;
    }
    return acc;
  }, {});

  // Merge table data in to combos and sort the tables using a natural sort
  const activeFilledCombos = floorplan.tableCombination.reduce<NamedTableCombination[]>(
    (acc, combo) => {
      if (
        combo.isArchived ||
        !combo.tableId ||
        !tableMap[combo.tableId[0]] ||
        tableMap[combo.tableId[0]].room !== room
      ) {
        return acc;
      }

      acc.push({ name: getTableCombinationName(tableMap, combo.tableId), ...combo });
      return acc;
    },
    []
  );

  const compare = natsort();
  return activeFilledCombos.sort((a, b) => compare(a.name, b.name));
};

const getTableCombinationName = (tableMap: Record<number, dashboard.ITable>, tableIds: number[]) =>
  tableIds
    .reduce<string[]>((acc, id) => {
      const name = tableMap[id].name;
      return name ? [name, ...acc] : acc;
    }, [])
    .sort(natsort())
    .join(', ');

/**
 * Sorts seating templates by start time (ascending order).
 *
 * @todo More gracefully handle the case where two services start at the same time
 * perhaps put the shortest service first so it renders on top of the longer one
 */
export const getSortedSeatingTemplates = (seatingTemplates: dashboard.ISeatingTemplate[]) => {
  return seatingTemplates.sort((a, b) => {
    if (!a.startTime || !b.startTime) {
      throw new Error('Invalid seating template. All seating templates must have a start time.');
    }

    const startA = adjustForBusinessDay('2020-01-01', a.startTime);
    const startB = adjustForBusinessDay('2020-01-01', b.startTime);

    return startA.isBefore(startB) ? -1 : startA.isAfter(startB) ? 1 : 0;
  });
};

/**
 * An unassigned table is a table with a temporary ID that exists in the floor plan
 * and does not have a corresponding SeatingTemplateTable entry in each service
 */
export const hasUnassignedSeatingTemplateTables = ({
  floorplan,
  seatingTemplate,
}: dashboard.BusinessInventorySchedule): boolean => {
  const unpersistedTablesCount = getUnpersistedTables(floorplan).length;

  return seatingTemplate.some(
    (template) =>
      getUnpersistedTablesFromSeatingTemplate(template).length !== unpersistedTablesCount
  );
};

/**
 * Group unassigned tables by room for display
 */
export const getUnassignedSeatingTemplateTablesByRoom = ({
  floorplan,
  seatingTemplate,
}: dashboard.BusinessInventorySchedule) => {
  const assignedUnpersistedTableIds = seatingTemplate.reduce<Set<number>>((tableIds, template) => {
    const templateTables = getUnpersistedTablesFromSeatingTemplate(template);
    templateTables.forEach((t) => {
      t.tableId && tableIds.add(t.tableId);
    });

    return tableIds;
  }, new Set());

  const unpersistedTables = getUnpersistedTables(floorplan);

  return unpersistedTables.reduce<Record<string, dashboard.ITable[]>>((acc, table) => {
    if (table.id && !assignedUnpersistedTableIds.has(table.id) && table.room) {
      acc[table.room] = acc[table.room] ? acc[table.room].concat([table]) : [table];
    }
    return acc;
  }, {});
};

/**
 * Returns true if a floor plan contains any tables with temporary IDs
 */
export const hasUnpersistedTables = (floorplan?: dashboard.IFloorplan | null): boolean => {
  if (!floorplan || !floorplan.table) {
    return false;
  }

  return floorplan.table.some((t) => isTemporaryId(t.id));
};

/**
 * Unpersisted tables are floor plan tables with a temporary ID
 * This indicates they have not been saved to the schedule.
 */
const getUnpersistedTables = (floorplan?: dashboard.IFloorplan | null) => {
  if (!floorplan || !floorplan.table) {
    return [];
  }

  return floorplan.table.filter((t) => isTemporaryId(t.id));
};

/**
 * Unpersisted seating template tables contain temporary id values
 * This indicates they have not been saved to the schedule.
 */
const getUnpersistedTablesFromSeatingTemplate = (seatingTemplate: dashboard.ISeatingTemplate) => {
  if (!seatingTemplate.table) {
    return [];
  }

  return seatingTemplate.table.filter((t) => isTemporaryId(t.tableId));
};

interface ObjectWithId {
  id?: number | null;
}

/**
 * Creates a unique value to use as an ID on tables and table combinations
 * created on the UI. A negative ID implies that the object has been created
 * locally not persisted to the service layer.
 *
 * These values are only used by a single user for the duration of a visit to
 * the schedule flow and are NOT currently sent to the service layer.
 */
export const createTemporaryId = (existingObjects?: ObjectWithId[] | null) => {
  const existingIds = _.compact(existingObjects?.map((obj) => obj.id));
  const lowestId = Math.min(0, ...existingIds);
  return lowestId - 1;
};

/**
 * Check whether an ID is a temporary ID.
 */
export function isTemporaryId(id?: number | null): boolean {
  return _.isNumber(id) && id < 0;
}

/**
 * Given an array of blocks and a service time range, determine whether the
 * table has a block that encompasses the entire service time range.
 */
export function tableIsFullyBlocked(
  blocks: dashboard.ISeatingTemplateTableBlock[],
  serviceStart: string,
  serviceEnd: string
): boolean {
  const firstBlock = blocks[0];
  if (!firstBlock || !firstBlock.startTime || !firstBlock.endTime) {
    return false;
  }

  const serviceStartDateTime = adjustForBusinessDay(LocalDate.MIN, LocalTime.parse(serviceStart));
  const serviceEndDateTime = adjustForBusinessDay(LocalDate.MIN, LocalTime.parse(serviceEnd));
  const blockStartDateTime = adjustForBusinessDay(
    LocalDate.MIN,
    LocalTime.parse(firstBlock.startTime)
  );
  const blockEndDateTime = adjustForBusinessDay(LocalDate.MIN, LocalTime.parse(firstBlock.endTime));

  return (
    (blockStartDateTime.equals(serviceStartDateTime) ||
      blockStartDateTime.isBefore(serviceStartDateTime)) &&
    (blockEndDateTime.equals(serviceEndDateTime) || blockEndDateTime.isAfter(serviceEndDateTime))
  );
}

/**
 * Restrict existing blocks to the bounds of the passed service start/end times
 */
const intersectBlocks = (
  blocks: dashboard.ISeatingTemplateTableBlock[],
  serviceStart: string,
  serviceEnd: string
) => {
  const { start, end } = adjustRangeForBusinessDay(
    TEMPLATE_INFO_BUSINESS_DAY,
    serviceStart,
    serviceEnd
  );
  const currentBlocks = rangeSetFromRestrictions(
    blocks,
    ({ startTime }) => startTime,
    ({ endTime }) => endTime
  );

  return currentBlocks
    .intersect(range(start, end))
    .ranges()
    .map(({ low, high }) => ({
      startTime: low.toLocalTime().toString(),
      endTime: high.toLocalTime().toString(),
    }));
};

const intersectRanges = (
  bookableRanges: dashboard.ISeatingTemplateTableExperienceBookableRange[],
  serviceStart: string,
  serviceEnd: string
) => {
  const { start, end } = adjustRangeForBusinessDay(
    TEMPLATE_INFO_BUSINESS_DAY,
    serviceStart,
    serviceEnd
  );
  const currentRanges = rangeSetFromRestrictions(
    bookableRanges,
    ({ startTimeInclusive }) => startTimeInclusive,
    ({ endTimeExclusive }) => endTimeExclusive
  );

  return (
    currentRanges
      .intersect(range(start, end))
      .ranges()
      // Since start time is inclusive and end time exclusive in this use case, ranges with the same start / end time are not valid
      .filter(({ low, high }) => !low.equals(high))
      .map(({ low, high }) => ({
        startTimeInclusive: low.toLocalTime().toString(),
        endTimeExclusive: high.toLocalTime().toString(),
      }))
  );
};

export function isFullServiceApplication(
  conditionalRange: dashboard.SeatingTemplateConditionalRange
) {
  return !conditionalRange.bookableRanges;
}

export function isInactiveRange(conditionalRange: dashboard.SeatingTemplateConditionalRange) {
  return conditionalRange.bookableRanges?.bookableRange?.length === 0;
}

/**
 * Update seating template tables in response to both the old and new service
 * time range. This method will make sure that currently applied blocks and bookable ranges
 * fall within the new start and end times for the service and that the range of a fully blocked
 * table matches the new start/end time.
 */
export function updateSeatingTemplateTables(
  seatingTemplateTables: dashboard.ISeatingTemplateTable[],
  originalStartTime: string,
  originalEndTime: string,
  newStartTime: string,
  newEndTime: string
): dashboard.ISeatingTemplateTable[] {
  return seatingTemplateTables.map((seatingTemplateTable) => {
    const blocks = seatingTemplateTable.block ?? [];

    let updatedBlocks: dashboard.ISeatingTemplateTableBlock[] = [];

    if (blocks.length) {
      updatedBlocks = tableIsFullyBlocked(blocks, originalStartTime, originalEndTime)
        ? [{ startTime: newStartTime, endTime: newEndTime }]
        : intersectBlocks(blocks, newStartTime, newEndTime);
    }

    const ticketTypeConfigs = seatingTemplateTable.ticketTypeConfig ?? [];

    const updatedTicketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[] =
      ticketTypeConfigs.map((ticketTypeConfig) => {
        return {
          ...ticketTypeConfig,
          conditionalRanges: ticketTypeConfig.conditionalRanges?.map((conditionalRange) => {
            if (isFullServiceApplication(conditionalRange)) {
              // A full-service application before the service hours changeis still a full-service
              // application after the service hours change
              return conditionalRange;
            }
            return {
              ...conditionalRange,
              bookableRanges: {
                bookableRange: intersectRanges(
                  conditionalRange.bookableRanges?.bookableRange ?? [],
                  newStartTime,
                  newEndTime
                ),
              },
            };
          }),
        };
      });

    const { activeTicketTypeConfigs, inactiveTicketTypeIds } =
      splitTicketTypeConfigsByActiveState(updatedTicketTypeConfigs);

    const activeTicketTypeIds = seatingTemplateTable.ticketTypeId?.filter(
      (ticketTypeId) => !inactiveTicketTypeIds.includes(ticketTypeId)
    );

    return {
      ...seatingTemplateTable,
      block: updatedBlocks,
      ticketTypeConfig: activeTicketTypeConfigs,
      ticketTypeId: activeTicketTypeIds,
    };
  });
}

/**
 * This method will ensure that currently applied bookable ranges
 * fall within the new start and end times for the service.
 */
export function updateSeatingTemplateTableCombinations(
  seatingTemplateTableCombinations: dashboard.ISeatingTemplateTableCombination[],
  newStartTime: string,
  newEndTime: string
): dashboard.ISeatingTemplateTable[] {
  return seatingTemplateTableCombinations.map((seatingTemplateTableCombinations) => {
    const ticketTypeConfigs = seatingTemplateTableCombinations.ticketTypeConfig ?? [];

    const updatedTicketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[] =
      ticketTypeConfigs.map((ticketTypeConfig) => {
        return {
          ...ticketTypeConfig,
          conditionalRanges: ticketTypeConfig.conditionalRanges?.map((conditionalRange) => {
            if (isFullServiceApplication(conditionalRange)) {
              // A full-service application before the service hours changeis still a full-service
              // application after the service hours change
              return conditionalRange;
            }
            return {
              ...conditionalRange,
              bookableRanges: {
                bookableRange: intersectRanges(
                  conditionalRange.bookableRanges?.bookableRange ?? [],
                  newStartTime,
                  newEndTime
                ),
              },
            };
          }),
        };
      });

    const { activeTicketTypeConfigs } =
      splitTicketTypeConfigsByActiveState(updatedTicketTypeConfigs);

    return {
      ...seatingTemplateTableCombinations,
      ticketTypeConfig: activeTicketTypeConfigs,
    };
  });
}

/**
 * Remove any ticket type configs that are now inactive (have no valid bookable ranges) after manipulating.
 * Keep track of the relevant ticketTypeIds so that we can use these for removing them from seatingTemplateTable.ticketTypeId
 */
export function splitTicketTypeConfigsByActiveState(
  updatedTicketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[]
) {
  return updatedTicketTypeConfigs.reduce<{
    activeTicketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[];
    inactiveTicketTypeIds: number[];
  }>(
    (acc, ticketTypeConfig) => {
      const conditionalRanges = ticketTypeConfig.conditionalRanges || [];

      if (conditionalRanges.length === 0 || conditionalRanges.every(isInactiveRange)) {
        if (ticketTypeConfig.ticketTypeId) {
          acc.inactiveTicketTypeIds.push(ticketTypeConfig.ticketTypeId);
        }
      } else {
        acc.activeTicketTypeConfigs.push(ticketTypeConfig);
      }

      return acc;
    },
    { activeTicketTypeConfigs: [], inactiveTicketTypeIds: [] }
  );
}

export function updateSeatingTemplate(
  originalTemplate: dashboard.ISeatingTemplate,
  payload: dashboard.ISeatingTemplate
): dashboard.ISeatingTemplate {
  const newTemplate = { ...originalTemplate, ...payload };
  if (
    !originalTemplate.startTime ||
    !originalTemplate.endTime ||
    !payload.startTime ||
    !payload.endTime ||
    (originalTemplate.startTime === payload.startTime &&
      originalTemplate.endTime === payload.endTime)
  ) {
    // If we don't have complete time range information, or if the time range
    // hasn't changed, we don't need to do anything more.
    return newTemplate;
  }

  // If we know the time range has changed, we need to make some edits to the
  // seating template tables.

  // If we can book until end the endTime value must be extended 15 minutes to include it
  // as a bookable time (and render the square in the UI)
  const endTime = originalTemplate.canBookUntilEnd
    ? LocalTime.parse(payload.endTime).plusMinutes(15).toString()
    : payload.endTime;

  return {
    ...newTemplate,
    table: updateSeatingTemplateTables(
      newTemplate.table ?? [],
      originalTemplate.startTime,
      originalTemplate.endTime,
      payload.startTime,
      endTime
    ),
    tableCombination: updateSeatingTemplateTableCombinations(
      newTemplate.tableCombination ?? [],
      payload.startTime,
      endTime
    ),
  };
}

/**
 * Remove inactive seating template tables from a schedule, and remove experience and table sizing optimizations from tables
 * if disabled at the service-level.
 */
export const formatScheduleForPublish = (schedule: dashboard.BusinessInventorySchedule) => {
  const seatingTemplates = schedule.seatingTemplate;

  const formattedSeatingTemplates = seatingTemplates.map((template) => ({
    ...template,
    table: template.table
      ? template.table
          .filter((table) => {
            if (table.ticketTypeId?.length === 0) {
              return false;
            }
            // Now that ticketTypeId is an array, we may want to explore removing use of the sentinel
            // everywhere in favor of an empty array, but that is outside the scope of this bug fix (@bp-tock)
            const ticketTypeId = getTicketTypeIdFromSeatingTemplateTable(table);
            return ticketTypeId !== INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID;
          })
          .map<dashboard.ISeatingTemplateTable>((table) => {
            if (template.isExperienceOptimizationEnabled) {
              return table;
            }
            return removeExperienceOptimizationsFromTable(table);
          })
          .map<dashboard.ISeatingTemplateTable>((table) => {
            if (template.isTableSizingOptimizationEnabled) {
              return table;
            }
            return removeTableSizingOptimizationsFromTable(table);
          })
          // If a table's min purchase size is lowered to be less than a table sizing optimization rule's min party size,
          // we remove the now invalid optimization because we cannot have an optimization for a size larger or equal to the min
          .map<dashboard.ISeatingTemplateTable>((templateTable) => {
            const table = schedule.floorplan?.table?.find((t) => t.id === templateTable.tableId);

            return removeInvalidTableSizingOptimizationsFromTable(templateTable, table);
          })
      : [],
  }));

  const templatesWithFormattedTableCombos = formattedSeatingTemplates.map((template) => ({
    ...template,
    tableCombination: template.tableCombination
      ? template.tableCombination.map<dashboard.ISeatingTemplateTableCombination>(
          (templateTableCombo) => {
            const tableComboTableIds = schedule.floorplan?.tableCombination?.find(
              (combo) => combo.id === templateTableCombo.tableCombinationId
            )?.tableId;

            const templateTableTableIds = template.table?.map((table) => table?.tableId) ?? [];

            // If a combo contains an inactive table, we remove all ticket type ids from the combo
            if (!tableComboTableIds?.every((tableId) => templateTableTableIds.includes(tableId))) {
              return removeTicketTypeIdsFromTableCombo(templateTableCombo);
            }
            return templateTableCombo;
          }
        )
      : [],
  }));

  return {
    ...schedule,
    seatingTemplate: templatesWithFormattedTableCombos,
  };
};

const removeTicketTypeIdsFromTableCombo = (
  templateTableCombo: dashboard.ISeatingTemplateTableCombination
) => {
  return { ...templateTableCombo, ticketTypeConfig: [] };
};

export const removeInvalidTableSizingOptimizationsFromTable = (
  templateTable: dashboard.ISeatingTemplateTable,
  table: dashboard.ITable | undefined
) => {
  if (table === undefined) {
    return templateTable;
  }

  const updatedSizeConfigs = templateTable.tableSizeConfig?.filter((sizeConfig) => {
    return (
      sizeConfig.minPurchaseSize &&
      table?.minPartySize &&
      sizeConfig.minPurchaseSize < table.minPartySize
    );
  });

  return { ...templateTable, tableSizeConfig: updatedSizeConfigs };
};

function removeTableSizingOptimizationsFromTable(table: dashboard.ISeatingTemplateTable) {
  return { ...table, tableSizeConfig: [] };
}

function removeExperienceOptimizationsFromTable(table: dashboard.ISeatingTemplateTable) {
  const ticketTypeConfig =
    table.ticketTypeConfig?.map<dashboard.ISeatingTemplateTableTicketTypeConfig>((config) => {
      const { templateRange } = getConditionalRangeTemplateForSTTTicketTypeConfig(config);

      return {
        ...config,
        conditionalRanges: [{ bookableRanges: templateRange.bookableRanges }],
      };
    });

  return { ...table, ticketTypeConfig };
}

/**
 * Convert inactive SeatingTemplateTable to dashboard.ISeatingTemplateTable and vice-versa
 * This is needed because we are using the old blueprint UI which has different types
 * than the seating templates on business inventory schedule.
 *
 * In the legacy blueprint UI a _deleted: true indicates an inactive table.
 * In TACO a ticket type of INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID indicates inactive
 * This value is used because SeatingTemplateTable requires a ticketTypeId.
 */
export const legacyTemplateTablesToScheduleTemplateTables = (tables: SeatingTemplateTable[]) =>
  tables.map((table) => {
    const t = table._deleted
      ? { ...table, ticketTypeId: [INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID] }
      : table;
    delete t._deleted;

    return t;
  });

export const scheduleTemplateTablesToLegacyTemplateTables = (
  tables: dashboard.ISeatingTemplateTable[]
) => {
  return tables.map((table) => {
    const ticketTypeId = getTicketTypeIdFromSeatingTemplateTable(table);
    return ticketTypeId === INACTIVE_SEATING_TEMPLATE_EXPERIENCE_ID
      ? { ...table, _deleted: true }
      : table;
  });
};

export const isPastPublishedSchedule = (
  schedule: dashboard.IBusinessInventorySchedule,
  currentDateInBusinessTimeZone: LocalDate
) => {
  if (schedule.state !== 'PUBLISHED' || !schedule.endDate || schedule.endDate === '') {
    return false;
  }

  return LocalDate.parse(schedule.endDate).isBefore(currentDateInBusinessTimeZone);
};

/**
 * Returns summarized data about Pacing configs that's used to render the Pacing cards in the Availability Planning's Services drawer, Review Page, etc.
 */
export const getPacingSummaryLegacy = ({
  pacingBasic,
  pacingAdvanced,
}: dashboard.ISeatingTemplate): {
  isPacingEnabled: boolean;
  isServicePacingAdvanced: boolean;
  onlineCoversText: number | string;
  totalCoversText: number | string;
} => {
  const isPacingEnabled = pacingBasic?.applyPacingLimits ?? false;
  const isServicePacingAdvanced = (pacingAdvanced?.length ?? 0) > 0;

  return {
    isPacingEnabled,
    isServicePacingAdvanced,
    onlineCoversText: !isPacingEnabled
      ? '--'
      : isServicePacingAdvanced
        ? 'Advanced'
        : (pacingBasic?.maxOnlineCovers ?? ''),
    totalCoversText: !isPacingEnabled
      ? '--'
      : isServicePacingAdvanced
        ? 'Advanced'
        : (pacingBasic?.maxTotalCovers ?? ''),
  };
};

/**
 * Returns summarized data about Pacing configs that's used to render the Pacing cards in the Availability Planning's Services drawer, Review Page, etc.
 */
export const getPacingSummary = (
  { pacingBasic, pacingAdvanced, experiencePacingBasic }: dashboard.ISeatingTemplate,
  activeTicketTypeIdsForService: Set<number>
): {
  isPacingEnabled: boolean;
  onlineCoversCount: number;
  totalCoversCount: number;
  totalOverridesCount: number;
  includedExperiencesCount: number;
  experiencesInServiceCount: number;
} => {
  const isPacingEnabled = pacingBasic?.applyPacingLimits ?? false;
  const onlineCoversCount = !isPacingEnabled ? 0 : (pacingBasic?.maxOnlineCovers ?? 0);
  const totalCoversCount = !isPacingEnabled ? 0 : (pacingBasic?.maxTotalCovers ?? 0);
  const totalOverridesCount = pacingAdvanced?.length ?? 0;

  const ticketTypeIdsExcluded = new Set(
    experiencePacingBasic
      ?.filter((experiencePacingBasic) => experiencePacingBasic.excludeFromPacing)
      .map((experiencePacingBasic) => experiencePacingBasic.ticketTypeId)
  );

  const includedExperiencesCount = [...activeTicketTypeIdsForService]?.filter(
    (ticketTypeId) => !ticketTypeIdsExcluded.has(ticketTypeId)
  ).length;

  return {
    isPacingEnabled,
    onlineCoversCount,
    totalCoversCount,
    totalOverridesCount,
    includedExperiencesCount,
    experiencesInServiceCount: activeTicketTypeIdsForService.size,
  };
};

/**
 * Returns true if time range specific turns are present on a seating template.
 */
export const getHasTurnsByRange = (seatingTemplate: dashboard.ISeatingTemplate) => {
  const turnsByRange = seatingTemplate.turnTimesBasic?.byTimeRange ?? [];

  return turnsByRange.length > 0;
};

/**
 * Returns true if experience based turns are present on a seating template.
 */
export const getHasTurnsByExperience = (seatingTemplate: dashboard.ISeatingTemplate) => {
  const turnsByExperience = seatingTemplate.turnTimesByTicketType ?? [];

  return turnsByExperience.length > 0;
};

/**
 * Returns true if advanced turns are present on a seating template.
 * "Advanced" is defined as the presence of any experience or range based turns.
 */
export const getHasAdvancedTurns = (seatingTemplate: dashboard.ISeatingTemplate) => {
  return getHasTurnsByRange(seatingTemplate) || getHasTurnsByExperience(seatingTemplate);
};

/**
 * Returns true if the supplied turn times deviate from the application defaults.
 */
export const getHasCustomTurnTimes = (turns: dashboard.ISingleTurnTime[]) => {
  const basicTurnsMap = getDefaultTurnTimeFields().reduce<{ [partySize: number]: number }>(
    (acc, turn) => ({
      ...acc,
      [turn.partySize]: Number(turn.turnTimeMinutes),
    }),
    {}
  );

  return turns.some(
    (turn) => !turn.partySize || basicTurnsMap[turn.partySize] !== turn.turnTimeMinutes
  );
};

export const getTurnTimesSummary = (seatingTemplate: dashboard.ISeatingTemplate) => {
  const hasTurnsByRange = getHasTurnsByRange(seatingTemplate);
  const hasTurnsByExperience = getHasTurnsByExperience(seatingTemplate);

  return {
    turnsByRangeText: hasTurnsByRange ? 'Advanced' : 'No',
    turnsByExperienceText: hasTurnsByExperience ? 'Advanced' : 'No',
  };
};

/**
 * Convenience wrapper around getting the first ticket type id in a list,
 * implemented as part of the multi-experience tables project which modified
 * the SeatingTemplateTable.ticketTypeId proto field from optional to repeated.
 */
export const getTicketTypeIdFromSeatingTemplateTable = (
  seatingTemplateTable: dashboard.ISeatingTemplateTable | undefined
) => {
  return seatingTemplateTable?.ticketTypeId?.[0];
};
